import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AttorneyCard from "../components/AttorneyCard";
import ConsumerChat from "../components/ConsumerChat";
import useSEO from "../lib/useSEO";
import { api } from "../lib/api";
import { ArrowLeft, MapPin, AlertCircle, Users, MessageCircleQuestion, Loader2 } from "lucide-react";


const TIER_RANK = { top_spot: 0, featured: 1, basic: 2 };

export default function ResultsPage() {
    const [data, setData] = useState(null);
    const [showAttorneys, setShowAttorneys] = useState(false);
    const [questions, setQuestions] = useState(null);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [questionsError, setQuestionsError] = useState("");
    const navigate = useNavigate();

    useSEO({
        title: "Your Results",
        description: "Your personalized legal information and local attorney matches.",
        path: "/results",
        noindex: true,
    });

    useEffect(() => {
        const raw = sessionStorage.getItem("ll_last_result");
        if (!raw) { navigate("/"); return; }
        try { setData(JSON.parse(raw)); } catch { navigate("/"); }
    }, [navigate]);

    if (!data) return null;

    const sortedAttorneys = (data.attorneys || []).slice().sort(
        (a, b) => (TIER_RANK[a.tier] ?? 99) - (TIER_RANK[b.tier] ?? 99)
    );
    const attorneyCount = sortedAttorneys.length;

    const loadQuestions = async () => {
        if (questions || questionsLoading) return;
        setQuestionsLoading(true);
        setQuestionsError("");
        try {
            const { data: resp } = await api.post("/questions/generate", {
                practice_area: data.category?.name || "",
                state: data.state?.name || data.state?.code || "",
                user_description: data.question || "",
            });
            setQuestions(resp.questions || []);
        } catch (e) {
            setQuestionsError(e?.response?.data?.detail || "Couldn't generate questions right now. Please try again.");
        } finally {
            setQuestionsLoading(false);
        }
    };

    const trackFindAttorneyClick = () => {
        // Fire-and-forget analytics ping — don't block UI on it.
        api.post("/track/attorney-view", {
            category_slug: data.category?.slug,
            zip_code: data.zip,
            county_id: data.county?.id,
            state_code: data.state?.code,
            event: "find_attorney_click",
        }).catch(() => { /* swallow — UX should never depend on analytics */ });
    };

    return (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-link">
                <ArrowLeft className="h-4 w-4" /> Ask another question
            </Link>

            <div className="ll-label mb-2">Your Question</div>
            <p className="font-serif text-2xl sm:text-3xl tracking-tight mb-6" data-testid="results-question">"{data.question}"</p>

            <div className="flex flex-wrap gap-2 mb-8">
                <span className="ll-pill" data-testid="results-category">{data.category?.name}</span>
                {data.county ? (
                    <span className="ll-pill bg-accent text-accent-foreground" data-testid="results-county">
                        <MapPin className="h-3 w-3 mr-1" />{data.county.name}, {data.state?.code}
                    </span>
                ) : (
                    <span className="ll-pill bg-muted text-muted-foreground">
                        ZIP {data.zip} — outside current coverage
                    </span>
                )}
            </div>

            {/* AI CHAT — multi-turn conversation, stays open across follow-ups */}
            <div className="mb-4" data-testid="ai-response">
                <ConsumerChat
                    initialQuestion={data.question}
                    initialAnswer={data.ai_response}
                    initialSources={data.sources}
                    initialProvidersSucceeded={data.providers_succeeded}
                    initialConfidenceSignal={data.confidence_signal}
                    categorySlug={data.category?.slug}
                    categoryName={data.category?.name}
                    zipCode={data.zip}
                    countyName={data.county?.name}
                    stateCode={data.state?.code}
                    sessionKey={`${data.category?.slug || "general"}-${data.zip || "unknown"}`}
                />
            </div>

            {/* QUESTIONS TO ASK AN ATTORNEY — separate tool, generates a printable checklist */}
            <div className="ll-card mb-4">
                <div className="mt-2">
                    {!questions && !questionsLoading && !questionsError && (
                        <button
                            onClick={loadQuestions}
                            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 text-primary px-5 py-2.5 text-sm font-semibold hover:bg-primary/10 transition-colors"
                            data-testid="generate-questions-btn">
                            <MessageCircleQuestion className="h-4 w-4" />
                            Questions to Ask an Attorney
                        </button>
                    )}
                    {questionsLoading && (
                        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="questions-loading">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Preparing questions for your situation…
                        </div>
                    )}
                    {questionsError && (
                        <div className="space-y-3">
                            <div className="text-sm text-destructive" data-testid="questions-error">{questionsError}</div>
                            <button onClick={() => { setQuestionsError(""); loadQuestions(); }}
                                className="text-sm text-primary hover:underline" data-testid="questions-retry-btn">Try again</button>
                        </div>
                    )}
                    {questions && questions.length > 0 && (
                        <div data-testid="questions-list">
                            <div className="flex items-center gap-2 mb-3">
                                <MessageCircleQuestion className="h-5 w-5 text-primary" />
                                <h3 className="font-serif text-lg font-semibold">Questions to ask an attorney</h3>
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">
                                Bring these to your call or consultation. They're starting points — feel free to add your own.
                            </p>
                            <ol className="space-y-3 list-none">
                                {questions.map((q, i) => (
                                    <li key={i} className="flex gap-3 text-sm leading-relaxed" data-testid={`question-${i}`}>
                                        <span className="font-semibold text-primary tabular-nums shrink-0">{i + 1}.</span>
                                        <span className="text-foreground">{q}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-10 p-4 rounded-md border border-border bg-accent/40 text-xs text-muted-foreground flex gap-2">
                <AlertCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <span>{data.disclaimer}</span>
            </div>

            {/* OPTIONAL: Find an attorney */}
            {!showAttorneys ? (
                <div className="ll-card text-center py-10" data-testid="find-attorney-prompt">
                    <Users className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-serif text-2xl font-semibold mb-2">Want to talk to a local attorney?</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                        Optional. We'll show attorneys in <strong className="text-foreground">{data.county ? `${data.county.name}, ${data.state?.code}` : `your ZIP area`}</strong> who handle <strong className="text-foreground">{data.category?.name}</strong>.
                    </p>
                    <button
                        onClick={() => { trackFindAttorneyClick(); setShowAttorneys(true); }}
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition-colors"
                        data-testid="find-attorney-btn">
                        Find an attorney in my area
                    </button>
                    <div className="mt-3 text-xs text-muted-foreground">No account required.</div>
                </div>
            ) : (
                <div data-testid="attorney-section">
                    <div className="ll-label mb-3">Local attorneys{data.county ? ` in ${data.county.name}` : ""}</div>
                    <h3 className="font-serif text-2xl font-semibold tracking-tight mb-1">
                        {attorneyCount} attorney{attorneyCount === 1 ? "" : "s"} matched your area
                    </h3>
                    <p className="text-xs text-muted-foreground mb-5">
                        Sorted by tier: Elite → Pro → Basic. Sponsored attorney visibility.
                    </p>

                    {attorneyCount === 0 ? (
                        <div className="ll-card text-sm text-muted-foreground" data-testid="no-attorneys">
                            No sponsored attorneys are visible in this county and category yet. Coverage expands as attorneys subscribe to Basic, Pro, or Elite placement. You can still use the legal information above to take your next step.
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="attorney-list">
                            {sortedAttorneys.map((a) => <AttorneyCard key={a.id} attorney={a} county={data.county} state={data.state} />)}
                        </div>
                    )}

                    <button
                        onClick={() => setShowAttorneys(false)}
                        className="mt-6 text-sm text-muted-foreground hover:text-primary"
                        data-testid="hide-attorney-btn">
                        Hide attorney listings
                    </button>
                </div>
            )}
        </section>
    );
}
